import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  Agent,
  AgentId,
  AgentStatus,
  BrainCategoryNode,
  BrainChatAction,
  BrainChatResponse,
  BrainHierarchy,
  BrainItem,
  BrainItemsResponse,
  BrainMatch,
  BrainNode,
  BrainStats,
} from '@shared/types';
import { Layout } from '../components/Layout';
import { Orb } from '../components/Orb';
import { Markdown } from '../components/Markdown';
import { api, ApiError } from '../lib/api';
import { useAssetUrl } from '../lib/useAssetUrl';
import { nodeStatusLabel, nodeStatusTip } from '../lib/nodeStatus';

/* ============================================================
   Brain Hub (Brain v3, build-spec/BRAIN-V3-SPEC.md).
   The Brains page is now a clean, fast Knowledge Hub: per-brain
   stats with a date range, category shelves → item lists → item
   pages, instant search, and a grounded chat dock with receipts.
   The 3D room lives on, unchanged, behind the "✦ Node view"
   button at /brains/:agent/nodes.
   ============================================================ */

type BrainId = AgentId | 'company';

interface BrainMeta {
  id: BrainId;
  name: string;
  color: string;
}

// Signature colors mirror orbs.css; 'company' = the shared brain (ice).
const BRAINS: BrainMeta[] = [
  { id: 'company', name: 'Company', color: '#5BD1F5' },
  { id: 'emma', name: 'Emma', color: '#5BD1F5' },
  { id: 'milo', name: 'Milo', color: '#4D7CFF' },
  { id: 'leo', name: 'Leo', color: '#46E0A3' },
  { id: 'quinn', name: 'Quinn', color: '#8FA3B0' },
  { id: 'iris', name: 'Iris', color: '#9D8CFF' },
  { id: 'nora', name: 'Nora', color: '#FF6B5E' },
  { id: 'grace', name: 'Grace', color: '#FFC24D' },
];
const BRAIN_IDS = BRAINS.map((b) => b.id);

function isBrainId(x: string | undefined): x is BrainId {
  return !!x && (BRAIN_IDS as string[]).includes(x);
}

/* ------------------------------------------------------------ helpers */

function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    const b = e.body as { error?: unknown } | null;
    if (b && typeof b.error === 'string') return b.error;
    return `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : 'Something went wrong';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/** Short relative time for list rows: '3h ago', '2d ago', else the date. */
function fmtAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return fmtDate(iso);
}

function fmtUsd(v: number): string {
  if (v === 0) return '$0';
  return v >= 1 ? `$${v.toFixed(2)}` : `$${v.toFixed(3)}`;
}

/** Where a note came from → a tiny glyph + plain label (the Field-Notes row anatomy). */
function origin(source: string | null | undefined): { glyph: string; label: string } {
  const s = (source ?? '').toLowerCase();
  if (s.includes('parker')) return { glyph: '✦', label: 'Parker' };
  if (s.includes('founder')) return { glyph: '✎', label: 'Founder' };
  if (s.includes('git')) return { glyph: '⌁', label: 'GitHub' };
  if (s.includes('agent') || s.includes('learn') || s.includes('pass')) return { glyph: '◍', label: 'Learned' };
  if (!s) return { glyph: '❋', label: 'Filed' };
  return { glyph: '❋', label: source ?? 'Filed' };
}

/** Flatten the category tree for pickers and lookups. */
function flatCategories(roots: BrainCategoryNode[]): BrainCategoryNode[] {
  const out: BrainCategoryNode[] = [];
  const walk = (nodes: BrainCategoryNode[], depth: number) => {
    for (const n of nodes) {
      out.push({ ...n, name: depth > 0 ? `${'  '.repeat(depth)}${n.name}` : n.name });
      walk(n.children, depth + 1);
    }
  };
  walk(roots, 0);
  return out;
}

function findCategory(roots: BrainCategoryNode[], id: string): BrainCategoryNode | null {
  for (const n of roots) {
    if (n.id === id) return n;
    const hit = findCategory(n.children, id);
    if (hit) return hit;
  }
  return null;
}

/* ------------------------------------------------------ date ranges */

type PresetKey = 'yesterday' | '7d' | '30d' | 'month' | 'lastyear' | 'custom';

const PRESETS: Array<{ key: PresetKey; label: string }> = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'month', label: 'This month' },
  { key: 'lastyear', label: 'Last year' },
  { key: 'custom', label: 'Custom' },
];

function presetRange(k: Exclude<PresetKey, 'custom'>): { from: string; to: string } {
  const day = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  const today = day(Date.now());
  switch (k) {
    case 'yesterday': {
      const y = day(Date.now() - 86400000);
      return { from: y, to: y };
    }
    case '7d':
      return { from: day(Date.now() - 6 * 86400000), to: today };
    case '30d':
      return { from: day(Date.now() - 29 * 86400000), to: today };
    case 'month':
      return { from: `${today.slice(0, 8)}01`, to: today };
    case 'lastyear': {
      const y = new Date().getUTCFullYear() - 1;
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    }
  }
}

/* ------------------------------------------------------ tiny pieces */

/** Wrap the matched span of a returned string in <mark>. */
function Hi({ text, match }: { text: string; match?: BrainMatch }) {
  if (!match || match.start < 0 || match.start + match.length > text.length) return <>{text}</>;
  return (
    <>
      {text.slice(0, match.start)}
      <mark>{text.slice(match.start, match.start + match.length)}</mark>
      {text.slice(match.start + match.length)}
    </>
  );
}

/** Founder-supplied portrait (assets/agents/<id>/portrait-<state>.png in R2) or the orb. */
function Identity({ brain, status }: { brain: BrainId; status: AgentStatus | undefined }) {
  const state = status === 'waiting_founder' ? 'waiting' : status === 'off' || !status ? 'idle' : status;
  const url = useAssetUrl(`agents/${brain}/portrait-${state}.png`);
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  if (url && !broken) {
    return <img className="bh-portrait" src={url} alt={`${brain} portrait`} onError={() => setBroken(true)} />;
  }
  const orbAgent: AgentId = brain === 'company' ? 'emma' : brain;
  const orbState: AgentStatus = status ?? 'idle';
  return (
    <span className="bh-orbwrap">
      <Orb agent={orbAgent} size="md" state={orbState} />
    </span>
  );
}

/** One item row: status dot + origin glyph + title + takeaway + updated. */
function ItemRow({ item, onOpen }: { item: BrainItem; onOpen: (id: string) => void }) {
  const og = origin(item.source);
  const titleMatch = item.matches?.find((m) => m.field === 'title');
  const takeawayMatch = item.matches?.find((m) => m.field === 'takeaway');
  return (
    <button type="button" className={`bh-row${item.status === 'retired' ? ' retired' : ''}`} onClick={() => onOpen(item.id)}>
      <i className={`st ${item.status}`} title={nodeStatusTip(item.status)} aria-label={`Status: ${nodeStatusLabel(item.status)}`} />
      <span className="og" title={og.label} aria-hidden>
        {og.glyph}
      </span>
      <span className="mid">
        <span className="ttl">
          <Hi text={item.title} match={titleMatch} />
        </span>
        {item.takeaway && (
          <span className="tk">
            <Hi text={item.takeaway} match={takeawayMatch} />
          </span>
        )}
      </span>
      <span className="upd mono">{fmtAgo(item.updated_at)}</span>
    </button>
  );
}

/* ------------------------------------------------------ stats strip */

function StatsStrip({ agent }: { agent: BrainId }) {
  const [preset, setPreset] = useState<PresetKey>('7d');
  const [range, setRange] = useState(() => presetRange('7d'));
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [stats, setStats] = useState<BrainStats | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setStats(null);
    setFailed(false);
    api
      .get<BrainStats>(`/brains/${agent}/stats?from=${range.from}&to=${range.to}`)
      .then((s) => alive && setStats(s))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [agent, range]);

  const pickPreset = (k: PresetKey) => {
    setPreset(k);
    if (k !== 'custom') setRange(presetRange(k));
  };
  const applyCustom = () => {
    if (!customFrom || !customTo || customFrom > customTo) return;
    setRange({ from: customFrom, to: customTo });
  };

  // Daily cost bars — bucket wide ranges so the strip never overflows.
  const bars = useMemo(() => {
    const s = stats?.cost_series ?? [];
    if (s.length <= 62) return s.map((d) => ({ label: d.day, cost: d.cost, runs: d.runs }));
    const size = Math.ceil(s.length / 62);
    const out: Array<{ label: string; cost: number; runs: number }> = [];
    for (let i = 0; i < s.length; i += size) {
      const chunk = s.slice(i, i + size);
      out.push({
        label: `${chunk[0].day} – ${chunk[chunk.length - 1].day}`,
        cost: chunk.reduce((a, b) => a + b.cost, 0),
        runs: chunk.reduce((a, b) => a + b.runs, 0),
      });
    }
    return out;
  }, [stats]);
  const maxCost = Math.max(0.000001, ...bars.map((b) => b.cost));

  return (
    <div className="bh-statscard card">
      <div className="bh-range" role="group" aria-label="Date range">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`bh-rpill${preset === p.key ? ' on' : ''}`}
            onClick={() => pickPreset(p.key)}
          >
            {p.label}
          </button>
        ))}
        {preset === 'custom' && (
          <span className="bh-custom">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} aria-label="From" />
            <span className="dim">→</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} aria-label="To" />
            <button type="button" className="btn btn-line sm" onClick={applyCustom} disabled={!customFrom || !customTo || customFrom > customTo}>
              Apply
            </button>
          </span>
        )}
      </div>

      {failed ? (
        <p className="small" style={{ marginTop: 10 }}>
          Couldn't load the numbers for this range — try again shortly.
        </p>
      ) : (
        <>
          <div className="bh-stats">
            <div className="bh-stat">
              <p className="t-label">Cost in range</p>
              <div className="num mono">{stats ? fmtUsd(stats.cost_usd) : '…'}</div>
            </div>
            <div className="bh-stat">
              <p className="t-label">Notes total</p>
              <div className="num mono">{stats ? stats.items_total : '…'}</div>
            </div>
            <div className="bh-stat">
              <p className="t-label">New in range</p>
              <div className="num mono">{stats ? stats.items_new : '…'}</div>
            </div>
            <div className="bh-stat">
              <p className="t-label">Categories</p>
              <div className="num mono">{stats ? stats.category_count : '…'}</div>
            </div>
          </div>
          {stats && bars.length > 1 && (
            <div className="bh-bars" aria-label="Daily cost">
              {bars.map((b, i) => (
                <span
                  key={i}
                  className="bh-bar"
                  title={`${b.label}: ${fmtUsd(b.cost)} · ${b.runs} run${b.runs === 1 ? '' : 's'}`}
                  style={{ height: `${b.cost <= 0 ? 6 : Math.max(10, Math.round((b.cost / maxCost) * 100))}%`, opacity: b.cost <= 0 ? 0.25 : 1 }}
                />
              ))}
            </div>
          )}
          {stats && stats.top_sources.length > 0 && (
            <div className="bh-sources">
              <span className="t-label" style={{ marginRight: 4 }}>
                Top sources
              </span>
              {stats.top_sources.map((s) => (
                <span key={s.source} className="chip chip-dim">
                  {s.source} · {s.count}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------ chat dock */

interface ChatTurn {
  q: string;
  res?: BrainChatResponse;
  error?: string;
  shown: number; // characters revealed so far (streaming feel)
  applied: Set<number>; // indices of proposed_actions already applied
}

function ChatDock({
  agent,
  brainName,
  onOpenItem,
  onOrganised,
}: {
  agent: BrainId;
  brainName: string;
  onOpenItem: (id: string) => void;
  onOrganised: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [left, setLeft] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // The Company Brain has no agent of its own — research requests go to Quinn.
  const askAgent: AgentId = agent === 'company' ? 'quinn' : agent;
  const askName = askAgent.charAt(0).toUpperCase() + askAgent.slice(1);

  // Streaming-feel reveal: advance the newest answered turn a chunk at a time.
  useEffect(() => {
    const last = turns[turns.length - 1];
    if (!last?.res || last.shown >= last.res.answer_md.length) return;
    const t = setInterval(() => {
      setTurns((ts) => {
        const idx = ts.length - 1;
        const cur = ts[idx];
        if (!cur?.res || cur.shown >= cur.res.answer_md.length) return ts;
        const next = [...ts];
        next[idx] = { ...cur, shown: Math.min(cur.res.answer_md.length, cur.shown + 28) };
        return next;
      });
    }, 30);
    return () => clearInterval(t);
  }, [turns]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, busy]);

  const send = useCallback(async () => {
    const question = q.trim();
    if (!question || busy) return;
    setQ('');
    setBusy(true);
    setTurns((ts) => [...ts, { q: question, shown: 0, applied: new Set() }]);
    try {
      const res = await api.post<BrainChatResponse>(`/brains/${agent}/chat`, { question });
      setLeft(res.turns_left_today);
      setTurns((ts) => {
        const next = [...ts];
        next[next.length - 1] = { ...next[next.length - 1], res, shown: 0 };
        return next;
      });
    } catch (e) {
      setTurns((ts) => {
        const next = [...ts];
        next[next.length - 1] = { ...next[next.length - 1], error: errMsg(e) };
        return next;
      });
    } finally {
      setBusy(false);
    }
  }, [q, busy, agent]);

  const applyAction = useCallback(
    async (turnIdx: number, actionIdx: number, action: BrainChatAction) => {
      try {
        if (action.kind === 'move_item') {
          await api.patch(`/brains/node/${action.node_id}/category`, { category_id: action.category_id });
        } else {
          await api.patch(`/brains/categories/${action.category_id}`, { name: action.name });
        }
        setTurns((ts) =>
          ts.map((t, i) => (i === turnIdx ? { ...t, applied: new Set([...t.applied, actionIdx]) } : t)),
        );
        onOrganised();
      } catch (e) {
        window.alert(errMsg(e));
      }
    },
    [onOrganised],
  );

  const research = useCallback(
    async (question: string) => {
      try {
        await api.post('/feed/messages', { body_md: `@${askAgent} ${question}`, kind: 'question' });
        window.alert(`Sent to ${askName} — the reply lands in the Feed.`);
      } catch (e) {
        window.alert(errMsg(e));
      }
    },
    [askAgent, askName],
  );

  /** Answer text with inline numbered citation chips replacing [item:id] markers. */
  const renderAnswer = (t: ChatTurn): ReactNode => {
    if (!t.res) return null;
    let text = t.res.answer_md.slice(0, t.shown);
    // Mid-reveal, hide a half-typed citation marker ("[item:ab…") so raw
    // bracket text never flashes before it becomes a chip.
    if (t.shown < t.res.answer_md.length) text = text.replace(/\[[^\]]{0,30}$/, '');
    const numById = new Map(t.res.citations.map((c, i) => [c.id, i + 1]));
    const parts: ReactNode[] = [];
    const re = /\[item:([^\]\s]+)\]/g;
    let last = 0;
    let k = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      if (m.index > last) parts.push(<span key={k++}>{text.slice(last, m.index)}</span>);
      const cid = m[1];
      const n = numById.get(cid);
      const cite = t.res.citations.find((c) => c.id === cid);
      if (n && cite) {
        parts.push(
          <button key={k++} type="button" className="bh-cite" title={cite.title} onClick={() => onOpenItem(cid)}>
            {n}
          </button>,
        );
      }
      last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(<span key={k++}>{text.slice(last)}</span>);
    return <div className="ans">{parts}</div>;
  };

  return (
    <div className={`bh-dock${open ? ' open' : ''}`}>
      {!open ? (
        <button type="button" className="bh-dockpill" onClick={() => setOpen(true)}>
          ✦ Ask this brain
        </button>
      ) : (
        <div className="bh-dockpanel">
          <div className="bh-dockhead">
            <span>
              Ask {brainName}'s brain
              {left !== null && <span className="small" style={{ marginLeft: 8 }}>{left} questions left today</span>}
            </span>
            <button type="button" className="x" onClick={() => setOpen(false)} aria-label="Close chat">
              ×
            </button>
          </div>
          <div className="bh-dockbody" ref={scrollRef}>
            {turns.length === 0 && (
              <p className="small" style={{ padding: '6px 2px' }}>
                Ask in plain English — answers come only from what this brain has learned, with receipts you can
                tap to open the exact note.
              </p>
            )}
            {turns.map((t, ti) => {
              const done = !!t.res && t.shown >= t.res.answer_md.length;
              return (
                <div key={ti} className="turn">
                  <div className="qmsg">{t.q}</div>
                  {t.error ? (
                    <div className="amsg err">{t.error}</div>
                  ) : !t.res ? (
                    <div className="amsg">
                      <span className="bh-dots" aria-label="Thinking">
                        <i />
                        <i />
                        <i />
                      </span>
                    </div>
                  ) : (
                    <div className="amsg">
                      {renderAnswer(t)}
                      {done && t.res.citations.length > 0 && (
                        <div className="cites">
                          {t.res.citations.map((c, i) => (
                            <button key={c.id} type="button" className="bh-citechip" onClick={() => onOpenItem(c.id)}>
                              <b className="mono">{i + 1}</b> {c.title}
                            </button>
                          ))}
                        </div>
                      )}
                      {done && t.res.citations.length === 0 && (
                        <button type="button" className="btn btn-line sm" style={{ marginTop: 8 }} onClick={() => void research(t.q)}>
                          Ask {askName} to research this
                        </button>
                      )}
                      {done &&
                        t.res.proposed_actions.map((a, ai) => (
                          <div key={ai} className="bh-action">
                            <span>
                              {a.kind === 'move_item'
                                ? `Move “${a.node_title}” into ${a.category_name}`
                                : `Rename category “${a.current_name}” to “${a.name}”`}
                            </span>
                            {t.applied.has(ai) ? (
                              <span className="chip chip-mint">Done</span>
                            ) : (
                              <button type="button" className="btn btn-mint sm" onClick={() => void applyAction(ti, ai, a)}>
                                Apply
                              </button>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="bh-composer">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void send();
              }}
              placeholder={`Ask ${brainName} anything it knows…`}
              aria-label="Question"
            />
            <button type="button" className="btn btn-primary sm" onClick={() => void send()} disabled={busy || !q.trim()}>
              {busy ? '…' : 'Ask'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------ item page */

function ItemPage({
  agent,
  nodeId,
  hierarchy,
  onBack,
  onOpenCategory,
  onChanged,
}: {
  agent: BrainId;
  nodeId: string;
  hierarchy: BrainHierarchy | null;
  onBack: () => void;
  onOpenCategory: (id: string) => void;
  onChanged: () => void;
}) {
  const [node, setNode] = useState<BrainNode | null>(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [moving, setMoving] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  // The Company Brain has no agent of its own — questions about it go to Quinn.
  const askAgent: AgentId = agent === 'company' ? 'quinn' : agent;
  const askName = askAgent.charAt(0).toUpperCase() + askAgent.slice(1);
  const brainName = BRAINS.find((b) => b.id === agent)?.name ?? agent;

  useEffect(() => {
    let alive = true;
    setNode(null);
    setFailed(false);
    setEditing(false);
    api
      .get<BrainNode>(`/brains/node/${nodeId}`)
      .then((n) => {
        if (alive) {
          setNode(n);
          setTitle(n.title);
          setBody(n.body ?? '');
        }
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [nodeId]);

  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 3600);
    return () => clearTimeout(t);
  }, [note]);

  const cats = useMemo(() => (hierarchy ? flatCategories(hierarchy.categories) : []), [hierarchy]);
  const category = node?.category_id && hierarchy ? findCategory(hierarchy.categories, node.category_id) : null;

  const save = async () => {
    if (!node) return;
    const t = title.trim();
    if (!t) {
      setNote("The title can't be empty.");
      return;
    }
    setSaving(true);
    try {
      const updated = await api.patch<BrainNode>(`/brains/node/${node.id}`, { title: t, body });
      setNode(updated);
      setEditing(false);
      setNote('Saved — this edit survives every re-sync.');
      onChanged();
    } catch (e) {
      setNote(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const move = async (categoryId: string) => {
    if (!node) return;
    setMoving(true);
    try {
      const updated = await api.patch<BrainNode>(`/brains/node/${node.id}/category`, {
        category_id: categoryId === '' ? null : categoryId,
      });
      setNode((n) => (n ? { ...n, category_id: updated.category_id, updated_at: updated.updated_at } : n));
      setNote(categoryId ? 'Moved.' : 'Taken off its shelf.');
      onChanged();
    } catch (e) {
      setNote(errMsg(e));
    } finally {
      setMoving(false);
    }
  };

  const retire = async () => {
    if (!node || node.status === 'retired') return;
    const reason = window.prompt(
      `Retire “${node.title}”?\n\nAdd a reason — it's filed as a counter-lesson and the runner commits + re-syncs the brain.`,
    );
    if (reason === null) return;
    const r = reason.trim();
    if (!r) {
      setNote('Retiring needs a reason.');
      return;
    }
    try {
      await api.post(`/brains/node/${node.id}/retire`, { reason: r });
      setNode((n) => (n ? { ...n, status: 'retired' } : n));
      setNote('Retired — the runner will commit and re-sync.');
      onChanged();
    } catch (e) {
      setNote(errMsg(e));
    }
  };

  const ask = async () => {
    if (!node) return;
    const q = window.prompt(`What do you want to ask ${askName} about “${node.title}”?`);
    if (q === null || !q.trim()) return;
    try {
      await api.post('/feed/messages', {
        body_md: `@${askAgent} ${q.trim()}\n\n(About the brain note “${node.title}”.)`,
        kind: 'question',
      });
      setNote(`Sent to @${askAgent} — the reply lands in the Feed.`);
    } catch (e) {
      setNote(errMsg(e));
    }
  };

  if (failed) {
    return (
      <div className="card">
        <p className="small">Couldn't load this note — it may have just been re-synced. Try again shortly.</p>
        <button type="button" className="btn btn-line sm" style={{ marginTop: 10 }} onClick={onBack}>
          ← Back
        </button>
      </div>
    );
  }
  if (!node) return <p className="small">Loading note…</p>;

  const og = origin(node.source);
  return (
    <div className="bh-item card">
      <nav className="bh-crumbs">
        <button type="button" onClick={onBack}>
          {brainName}'s brain
        </button>
        {category && (
          <>
            <span className="sep">/</span>
            <button type="button" onClick={() => onOpenCategory(category.id)}>
              {category.name}
            </button>
          </>
        )}
        <span className="sep">/</span>
        <span className="here">{node.title}</span>
      </nav>

      {editing ? (
        <div className="bh-edit">
          <input value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Title" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} aria-label="Body" />
          <div className="bh-actions">
            <button type="button" className="btn btn-primary sm" onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-ghost sm"
              onClick={() => {
                setEditing(false);
                setTitle(node.title);
                setBody(node.body ?? '');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="bh-itemhead">
            <i className={`st ${node.status}`} title={nodeStatusTip(node.status)} aria-label={`Status: ${nodeStatusLabel(node.status)}`} />
            <h2>{node.title}</h2>
          </div>
          <div className="bh-meta">
            <span
              className={`chip ${node.status === 'active' ? 'chip-mint' : node.status === 'testing' ? 'chip-violet' : 'chip-dim'}`}
              title={nodeStatusTip(node.status)}
            >
              {nodeStatusLabel(node.status)}
            </span>
            {node.confidence && <span className="chip chip-ice">confidence: {node.confidence}</span>}
            {node.founder_edited === 1 && <span className="chip chip-amber">edited by a founder</span>}
          </div>
          <div className="bh-body">
            {node.body ? <Markdown text={node.body} /> : <p className="small">This note has no body text yet.</p>}
          </div>
          <p className="receipt">
            {og.glyph} from {og.label}
            {node.path ? ` · ${node.path}` : ''} · created {fmtDate(node.created_at)} · updated {fmtDate(node.updated_at ?? node.created_at)}
          </p>
          <div className="bh-actions">
            <button type="button" className="btn btn-line sm" onClick={() => setEditing(true)}>
              Edit
            </button>
            <label className="bh-move">
              <span className="small">Move to</span>
              <select
                value={node.category_id ?? ''}
                disabled={moving}
                onChange={(e) => void move(e.target.value)}
                aria-label="Move to category"
              >
                <option value="">Not filed yet</option>
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="btn btn-danger sm" onClick={() => void retire()} disabled={node.status === 'retired'}>
              {node.status === 'retired' ? 'Retired' : 'Retire'}
            </button>
            <button type="button" className="btn btn-mint sm" onClick={() => void ask()}>
              Ask {askName} about this
            </button>
          </div>
        </>
      )}
      {note && <div className="toast" style={{ marginTop: 12 }}>{note}</div>}
    </div>
  );
}

/* ------------------------------------------------------ the page */

export default function BrainHub() {
  const nav = useNavigate();
  const params = useParams<{ agent?: string; catId?: string; nodeId?: string }>();
  const agent: BrainId = isBrainId(params.agent) ? params.agent : 'company';
  const catId = params.catId ?? null;
  const nodeId = params.nodeId ?? null;
  const meta = BRAINS.find((b) => b.id === agent)!;

  const [counts, setCounts] = useState<Record<string, number>>({});
  const [agents, setAgents] = useState<Agent[]>([]);
  const [hierarchy, setHierarchy] = useState<BrainHierarchy | null>(null);
  const [hierFailed, setHierFailed] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // search
  const [q, setQ] = useState('');
  const [results, setResults] = useState<BrainItemsResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [full, setFull] = useState(false);

  // category item list
  const [catItems, setCatItems] = useState<BrainItemsResponse | null>(null);
  const [catEditing, setCatEditing] = useState(false);
  const [catName, setCatName] = useState('');
  const [catDesc, setCatDesc] = useState('');
  const [catSaving, setCatSaving] = useState(false);

  const loadHierarchy = useCallback(() => {
    api
      .get<BrainHierarchy>(`/brains/${agent}/hierarchy`)
      .then((h) => {
        setHierarchy(h);
        setHierFailed(false);
      })
      .catch(() => setHierFailed(true));
  }, [agent]);

  useEffect(() => {
    api
      .get<Array<{ agent_id: string; count: number }>>('/brains')
      .then((rows) => {
        const out: Record<string, number> = {};
        for (const r of rows) out[r.agent_id] = r.count;
        setCounts(out);
      })
      .catch(() => {});
    api
      .get<Agent[]>('/agents')
      .then(setAgents)
      .catch(() => {});
  }, []);

  useEffect(() => {
    setHierarchy(null);
    setQ('');
    setResults(null);
    setFull(false);
    loadHierarchy();
  }, [agent, loadHierarchy]);

  // Debounced instant search.
  useEffect(() => {
    const needle = q.trim();
    if (!needle) {
      setResults(null);
      setSearching(false);
      setFull(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      api
        .get<BrainItemsResponse>(`/brains/${agent}/items?q=${encodeURIComponent(needle)}`)
        .then((r) => {
          setResults(r);
          setSearching(false);
        })
        .catch(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, agent]);

  // Category items when browsing a shelf.
  useEffect(() => {
    if (!catId || nodeId) {
      setCatItems(null);
      return;
    }
    let alive = true;
    setCatItems(null);
    setCatEditing(false);
    api
      .get<BrainItemsResponse>(`/brains/${agent}/items?category=${encodeURIComponent(catId)}`)
      .then((r) => alive && setCatItems(r))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [agent, catId, nodeId]);

  const openItem = useCallback((id: string) => nav(`/brains/${agent}/item/${id}`), [nav, agent]);
  const openCategory = useCallback((id: string) => nav(`/brains/${agent}/cat/${id}`), [nav, agent]);

  const seed = async () => {
    setSeeding(true);
    try {
      await api.post(`/brains/${agent}/categories/seed`);
      loadHierarchy();
    } catch (e) {
      window.alert(errMsg(e));
    } finally {
      setSeeding(false);
    }
  };

  const saveCategory = async () => {
    if (!catId) return;
    const name = catName.trim();
    if (!name) return;
    setCatSaving(true);
    try {
      await api.patch(`/brains/categories/${catId}`, { name, description: catDesc.trim() });
      setCatEditing(false);
      loadHierarchy();
    } catch (e) {
      window.alert(errMsg(e));
    } finally {
      setCatSaving(false);
    }
  };

  const status = agents.find((a) => a.id === agent)?.status;
  const category = catId && hierarchy ? findCategory(hierarchy.categories, catId) : null;
  const isUnfiled = catId === 'none';

  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && q.trim()) setFull(true);
    if (e.key === 'Escape') setQ('');
  };

  /** One category card. */
  const CatCard = ({ c }: { c: BrainCategoryNode }) => (
    <button type="button" className="bh-cat card" onClick={() => openCategory(c.id)}>
      <h3>{c.name}</h3>
      {c.description && <p className="desc">{c.description}</p>}
      <p className="foot mono">
        {c.total_count} note{c.total_count === 1 ? '' : 's'}
        {c.children.length > 0 && ` · ${c.children.length} sub`}
        {c.last_updated && ` · ${fmtAgo(c.last_updated)}`}
      </p>
    </button>
  );

  /** Grouped results (search or the unfiled shelf). */
  const Groups = ({ data, limit }: { data: BrainItemsResponse; limit?: number }) => (
    <>
      {data.groups.map((g, gi) => {
        const items = limit ? g.items.slice(0, limit) : g.items;
        return (
          <div key={g.category?.id ?? `none-${gi}`} className="bh-group">
            <div className="bh-ghead">
              {g.category ? (
                <button type="button" onClick={() => openCategory(g.category!.id)}>
                  {g.category.name}
                </button>
              ) : (
                <span>Not filed yet</span>
              )}
              <span className="mono small">{g.items.length}</span>
            </div>
            <div className="bh-rows">
              {items.map((it) => (
                <ItemRow key={it.id} item={it} onOpen={openItem} />
              ))}
            </div>
            {limit && g.items.length > limit && (
              <button type="button" className="bh-more" onClick={() => setFull(true)}>
                Show all {g.items.length} in {g.category?.name ?? 'Not filed yet'} — or press Enter for everything
              </button>
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <Layout
      title="Brains"
      actions={
        <button type="button" className="btn btn-line sm" onClick={() => nav(`/brains/${agent}/nodes`)}>
          ✦ Node view
        </button>
      }
    >
      <style>{CSS}</style>

      {/* brain selector pills */}
      <div className="bh-pills">
        {BRAINS.map((b) => {
          const on = b.id === agent;
          const c = counts[b.id];
          return (
            <button
              key={b.id}
              type="button"
              className={`bh-pill${on ? ' on' : ''}`}
              style={on ? { borderColor: b.color } : undefined}
              onClick={() => nav(`/brains/${b.id}`)}
            >
              <span className="dot" style={{ background: b.color }} />
              {b.name}
              {typeof c === 'number' && <span className="n mono">{c}</span>}
            </button>
          );
        })}
      </div>

      {nodeId ? (
        <ItemPage
          agent={agent}
          nodeId={nodeId}
          hierarchy={hierarchy}
          onBack={() => nav(`/brains/${agent}`)}
          onOpenCategory={openCategory}
          onChanged={loadHierarchy}
        />
      ) : catId ? (
        /* ------------------------------ one shelf ------------------------------ */
        <>
          <nav className="bh-crumbs">
            <button type="button" onClick={() => nav(`/brains/${agent}`)}>
              {meta.name}'s brain
            </button>
            <span className="sep">/</span>
            <span className="here">{isUnfiled ? 'Not filed yet' : (category?.name ?? 'Category')}</span>
          </nav>

          {!isUnfiled && category && (
            <div className="bh-cathead">
              {catEditing ? (
                <div className="bh-edit">
                  <input value={catName} onChange={(e) => setCatName(e.target.value)} aria-label="Category name" />
                  <textarea value={catDesc} onChange={(e) => setCatDesc(e.target.value)} rows={2} aria-label="Category description" />
                  <div className="bh-actions">
                    <button type="button" className="btn btn-primary sm" onClick={() => void saveCategory()} disabled={catSaving || !catName.trim()}>
                      {catSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" className="btn btn-ghost sm" onClick={() => setCatEditing(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <h2>{category.name}</h2>
                    {category.description && <p className="small">{category.description}</p>}
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost sm"
                    onClick={() => {
                      setCatName(category.name);
                      setCatDesc(category.description ?? '');
                      setCatEditing(true);
                    }}
                  >
                    Edit
                  </button>
                </>
              )}
            </div>
          )}

          {!isUnfiled && category && category.children.length > 0 && (
            <div className="bh-cats" style={{ marginBottom: 16 }}>
              {category.children.map((c) => (
                <CatCard key={c.id} c={c} />
              ))}
            </div>
          )}

          {!catItems ? (
            <p className="small">Loading notes…</p>
          ) : catItems.total === 0 ? (
            <p className="small">Nothing on this shelf yet.</p>
          ) : (
            <Groups data={catItems} />
          )}
        </>
      ) : (
        /* ------------------------------ hub root ------------------------------ */
        <>
          <div className="bh-head">
            <Identity brain={agent} status={status} />
            <div className="bh-who">
              <h2>{agent === 'company' ? 'The Company Brain' : `${meta.name}'s brain`}</h2>
              <p className="small">
                {hierarchy
                  ? `${hierarchy.total_items} note${hierarchy.total_items === 1 ? '' : 's'} on ${hierarchy.categories.length} shel${hierarchy.categories.length === 1 ? 'f' : 'ves'}`
                  : 'Loading…'}
              </p>
            </div>
          </div>

          <StatsStrip agent={agent} />

          <input
            className="bh-search"
            placeholder="Search everything this brain knows…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onSearchKey}
            aria-label="Search this brain"
          />

          {q.trim() ? (
            /* search results */
            !results ? (
              <p className="small">{searching ? 'Searching…' : ''}</p>
            ) : results.total === 0 ? (
              <p className="small">Nothing matches “{results.q}” in this brain.</p>
            ) : (
              <>
                <p className="small" style={{ margin: '2px 0 10px' }}>
                  {results.total} match{results.total === 1 ? '' : 'es'}
                  {!full && ' — press Enter to see everything'}
                </p>
                <Groups data={results} limit={full ? undefined : 5} />
              </>
            )
          ) : hierFailed ? (
            <p className="small">Couldn't load this brain's shelves — it may still be syncing. Try again shortly.</p>
          ) : !hierarchy ? (
            <p className="small">Loading shelves…</p>
          ) : hierarchy.total_items === 0 ? (
            <div className="card">
              <p className="small">This brain is still learning — nothing has been written into it yet.</p>
            </div>
          ) : (
            <>
              {hierarchy.categories.length === 0 && (
                <div className="card" style={{ marginBottom: 14 }}>
                  <p className="small" style={{ marginBottom: 10 }}>
                    This brain has {hierarchy.total_items} notes but no shelves yet. One tap sorts them into
                    categories you can rename any time.
                  </p>
                  <button type="button" className="btn btn-primary sm" onClick={() => void seed()} disabled={seeding}>
                    {seeding ? 'Sorting…' : 'Sort notes into shelves'}
                  </button>
                </div>
              )}
              <div className="bh-cats">
                {hierarchy.categories.map((c) => (
                  <CatCard key={c.id} c={c} />
                ))}
                {hierarchy.uncategorized_count > 0 && (
                  <button type="button" className="bh-cat card unfiled" onClick={() => openCategory('none')}>
                    <h3>Not filed yet</h3>
                    <p className="desc">Notes that haven't been put on a shelf.</p>
                    <p className="foot mono">
                      {hierarchy.uncategorized_count} note{hierarchy.uncategorized_count === 1 ? '' : 's'}
                    </p>
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}

      <ChatDock key={agent} agent={agent} brainName={meta.name} onOpenItem={openItem} onOrganised={loadHierarchy} />
    </Layout>
  );
}

/* ------------------------------------------------------------------ CSS */

const CSS = `
.bh-pills{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:18px}
.bh-pill{display:inline-flex;align-items:center;gap:8px;font-family:var(--font);font-size:13px;font-weight:550;
  color:var(--dim);background:var(--panel-2);border:1px solid var(--line);border-radius:999px;padding:7px 14px;cursor:pointer;transition:all .15s}
.bh-pill:hover{color:var(--text);border-color:var(--line-2)}
.bh-pill.on{color:var(--text)}
.bh-pill .dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
.bh-pill .n{font-size:10.5px;color:var(--faint)}

.bh-head{display:flex;align-items:center;gap:16px;margin-bottom:16px}
.bh-portrait{width:72px;height:72px;border-radius:16px;object-fit:cover;border:1px solid var(--line-2);flex-shrink:0}
.bh-orbwrap{width:72px;height:72px;display:grid;place-items:center;flex-shrink:0}
.bh-who h2{margin:0}

.bh-statscard{margin-bottom:16px}
.bh-range{display:flex;gap:6px;flex-wrap:wrap;align-items:center}
.bh-rpill{font-family:var(--mono);font-size:10.5px;letter-spacing:.06em;text-transform:uppercase;color:var(--dim);
  background:transparent;border:1px solid var(--line);border-radius:999px;padding:5px 11px;cursor:pointer}
.bh-rpill:hover{color:var(--text);border-color:var(--line-2)}
.bh-rpill.on{color:var(--ice);border-color:rgba(var(--ice-rgb),.5);background:rgba(var(--ice-rgb),.07)}
.bh-custom{display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap}
.bh-custom input{background:var(--panel-2);color:var(--text);border:1px solid var(--line-2);border-radius:8px;padding:5px 8px;font-family:var(--mono);font-size:12px}
.bh-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:10px;margin-top:14px}
.bh-stat .num{font-family:var(--mono);font-size:22px;margin-top:4px}
.bh-bars{display:flex;align-items:flex-end;gap:2px;height:44px;margin-top:14px}
.bh-bar{flex:1;min-width:2px;background:rgba(var(--ice-rgb),.55);border-radius:2px 2px 0 0}
.bh-sources{display:flex;gap:6px;flex-wrap:wrap;align-items:center;margin-top:12px}

.bh-search{width:100%;background:var(--panel-2);color:var(--text);border:1px solid var(--line-2);border-radius:var(--r-ctl);
  padding:12px 15px;font-family:var(--font);font-size:14.5px;outline:none;margin-bottom:14px}
.bh-search:focus{border-color:rgba(var(--ice-rgb),.5)}
mark{background:rgba(var(--amber-rgb),.4);color:inherit;border-radius:3px;padding:0 1px}

.bh-cats{display:grid;gap:12px;grid-template-columns:repeat(auto-fill,minmax(240px,1fr))}
.bh-cat{text-align:left;cursor:pointer;font-family:var(--font);color:var(--text)}
.bh-cat h3{margin:0;font-size:15.5px;font-weight:550}
.bh-cat .desc{font-size:12.5px;color:var(--dim);margin-top:5px;line-height:1.45}
.bh-cat .foot{font-size:10.5px;color:var(--faint);margin-top:10px}
.bh-cat.unfiled{border-style:dashed}

.bh-group{margin-bottom:16px}
.bh-ghead{display:flex;align-items:center;gap:8px;margin-bottom:8px}
.bh-ghead button,.bh-ghead span:first-child{font-family:var(--font);font-weight:600;font-size:12px;letter-spacing:.14em;
  text-transform:uppercase;color:var(--dim);background:none;border:none;cursor:pointer;padding:0}
.bh-ghead button:hover{color:var(--ice)}
.bh-more{background:none;border:none;color:var(--ice);font-size:12.5px;cursor:pointer;padding:6px 2px;font-family:var(--font)}

.bh-rows{display:flex;flex-direction:column;gap:8px}
.bh-row{display:flex;gap:11px;align-items:flex-start;text-align:left;width:100%;cursor:pointer;
  background:var(--panel-2);border:1px solid var(--line);border-radius:var(--r);padding:12px 14px;color:var(--text);font-family:var(--font);transition:border-color .15s}
.bh-row:hover{border-color:var(--line-2)}
.bh-row.retired{opacity:.55}
.bh-row .og{color:var(--dim);font-size:13px;margin-top:1px;flex-shrink:0}
.bh-row .mid{flex:1;min-width:0;display:flex;flex-direction:column;gap:3px}
.bh-row .ttl{font-size:13.5px;font-weight:550;line-height:1.35}
.bh-row .tk{font-size:12px;color:var(--dim);line-height:1.4;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.bh-row .upd{font-size:10.5px;color:var(--faint);flex-shrink:0;margin-top:2px}
i.st{width:8px;height:8px;border-radius:50%;flex-shrink:0;margin-top:5px;display:inline-block}
i.st.active{background:var(--mint);box-shadow:0 0 7px var(--mint)}
i.st.testing{background:var(--violet)}
i.st.retired{background:var(--faint)}

.bh-crumbs{display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:12.5px;margin-bottom:14px}
.bh-crumbs button{background:none;border:none;color:var(--ice);cursor:pointer;font-size:12.5px;padding:0;font-family:var(--font)}
.bh-crumbs .sep{color:var(--faint)}
.bh-crumbs .here{color:var(--dim);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:min(60vw,420px)}

.bh-cathead{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:14px}
.bh-cathead h2{margin:0}

.bh-item .bh-itemhead{display:flex;align-items:baseline;gap:10px}
.bh-item h2{margin:0;font-size:clamp(18px,2.4vw,24px)}
.bh-meta{display:flex;gap:8px;flex-wrap:wrap;margin-top:10px}
.bh-body{margin-top:14px;font-size:14px;line-height:1.6}
.bh-actions{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:14px}
.bh-move{display:inline-flex;align-items:center;gap:7px}
.bh-move select{background:var(--panel-2);color:var(--text);border:1px solid var(--line-2);border-radius:8px;padding:7px 9px;font-size:12.5px;max-width:220px}
.bh-edit{display:flex;flex-direction:column;gap:10px}
.bh-edit input,.bh-edit textarea{width:100%;background:var(--panel-2);color:var(--text);border:1px solid var(--line-2);
  border-radius:var(--r-ctl);padding:10px 12px;font-family:var(--font);font-size:14px;line-height:1.5;outline:none;resize:vertical}

/* chat dock */
.bh-dock{position:fixed;right:16px;bottom:16px;z-index:60;max-width:min(430px,calc(100vw - 24px))}
.bh-dockpill{display:inline-flex;align-items:center;gap:8px;background:var(--ice);color:var(--btn-primary-text);
  font-family:var(--font);font-weight:650;font-size:13.5px;border:none;border-radius:999px;padding:12px 20px;cursor:pointer;box-shadow:var(--glow-ice)}
.bh-dockpanel{width:min(430px,calc(100vw - 24px));background:var(--panel);border:1px solid var(--line-2);border-radius:var(--r-lg);
  box-shadow:0 18px 50px rgba(0,0,0,.45);display:flex;flex-direction:column;overflow:hidden}
.bh-dockhead{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:12px 14px;border-bottom:1px solid var(--line);font-weight:600;font-size:13.5px}
.bh-dockhead .x{background:none;border:none;color:var(--dim);font-size:20px;line-height:1;cursor:pointer;padding:0 2px}
.bh-dockbody{overflow-y:auto;max-height:46vh;padding:12px 14px;display:flex;flex-direction:column;gap:12px}
.turn{display:flex;flex-direction:column;gap:8px}
.qmsg{align-self:flex-end;background:rgba(var(--ice-rgb),.14);border:1px solid rgba(var(--ice-rgb),.3);border-radius:12px 12px 3px 12px;
  padding:8px 12px;font-size:13px;max-width:88%}
.amsg{align-self:flex-start;background:var(--panel-2);border:1px solid var(--line);border-radius:12px 12px 12px 3px;padding:9px 12px;font-size:13px;max-width:94%;min-width:60px}
.amsg.err{color:var(--coral);border-color:rgba(var(--coral-rgb),.4)}
.amsg .ans{white-space:pre-wrap;line-height:1.55;word-break:break-word}
.bh-cite{display:inline-flex;align-items:center;justify-content:center;min-width:17px;height:17px;border-radius:6px;margin:0 2px;
  background:rgba(var(--ice-rgb),.16);border:1px solid rgba(var(--ice-rgb),.4);color:var(--ice);font-family:var(--mono);font-size:10px;cursor:pointer;vertical-align:text-top;padding:0 3px}
.amsg .cites{display:flex;flex-direction:column;gap:6px;margin-top:10px}
.bh-citechip{display:flex;align-items:flex-start;gap:8px;text-align:left;background:rgba(var(--ice-rgb),.06);border:1px solid var(--line);
  border-radius:9px;padding:7px 10px;color:var(--text);font-size:12px;cursor:pointer;font-family:var(--font);line-height:1.35}
.bh-citechip:hover{border-color:rgba(var(--ice-rgb),.45)}
.bh-citechip b{color:var(--ice);font-weight:500}
.bh-action{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:10px;background:rgba(var(--mint-rgb),.06);
  border:1px solid rgba(var(--mint-rgb),.3);border-radius:9px;padding:8px 11px;font-size:12.5px}
.bh-composer{display:flex;gap:8px;padding:11px 12px;border-top:1px solid var(--line)}
.bh-composer input{flex:1;min-width:0;background:var(--panel-2);color:var(--text);border:1px solid var(--line-2);border-radius:999px;
  padding:9px 14px;font-family:var(--font);font-size:13.5px;outline:none}
.bh-composer input:focus{border-color:rgba(var(--ice-rgb),.5)}
.bh-dots{display:inline-flex;gap:4px;align-items:center;height:14px}
.bh-dots i{width:5px;height:5px;border-radius:50%;background:var(--dim);animation:bhblink 1.2s infinite}
.bh-dots i:nth-child(2){animation-delay:.2s}
.bh-dots i:nth-child(3){animation-delay:.4s}
@keyframes bhblink{0%,80%,100%{opacity:.25}40%{opacity:1}}

@media (max-width:600px){
  .bh-head{gap:12px}
  .bh-portrait,.bh-orbwrap{width:56px;height:56px}
  .bh-row .upd{display:none}
  .bh-dock{right:12px;bottom:12px}
}
`;
